import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { ROLES } from '../config/constants';
import { HutricsAuthProvider } from '../api/handler';
import { clearGlobalStore } from '../utils/global-storage';
import { USER_INFO_ATTRIBUTE } from '../api/metadata';

const AuthContext = React.createContext(undefined);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState(null);
  const [clientDisplayName, setClientDisplayName] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [slug, setSlug] = useState(null);
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const login = (clientIdOrSlug, isClientId, username, password, callback) => {
    clearGlobalStore();
    HutricsAuthProvider.login(username, password, clientIdOrSlug, isClientId, (authData) => {
      if (authData) {
        setUser(username);
        setUserDisplayName(authData.displayName ? authData.displayName : username);
        setClientDisplayName(authData.clientName);
        setClientId(authData.clientId);
        setUserId(authData.userId);
        setToken(authData.token);
        setSlug(authData.slug);
        setUserRole(authData.userRole ? authData.userRole : ROLES.ASSESSMENT_USER);
        setMetadata(authData.metadata);
        let navigatePath = `${window.location.pathname}${window.location.search}`;
        const storedAuthData = {
          ...authData,
          username,
          navigatePath,
          sessionExpires: Date.now() + authData.expires * 1000,
        };
        localStorage.setItem('authData', JSON.stringify(storedAuthData));
      } else {
        localStorage.removeItem('authData');
        setToken(null);
        setUserRole(null);
      }
      callback(!!authData?.token);
    });
  };

  const logout = useCallback(() => {
    clearGlobalStore();
    HutricsAuthProvider.logout(() => {
      const storedAuthData = localStorage.getItem('authData');
      let authData;
      if (storedAuthData) {
        authData = JSON.parse(storedAuthData);
      }
      localStorage.removeItem('authData');
      setToken(null);
      setUserRole(null);
      navigate(authData ? authData.navigatePath : '/', { replace: true });
    });
  }, [clientId, navigate]);

  const loadSession = useCallback(
    (callback) => {
      const storedAuthData = localStorage.getItem('authData');
      if (storedAuthData) {
        const authData = JSON.parse(storedAuthData);
        if (authData.sessionExpires > Date.now()) {
          setUser(authData.username);
          setUserDisplayName(authData.displayName ? authData.displayName : authData.username);
          setClientDisplayName(authData.clientName);
          setClientId(authData.clientId);
          setToken(authData.token);
          setSlug(authData.slug);
          setUserId(authData.userId);
          setUserRole(authData.userRole ? authData.userRole : ROLES.ASSESSMENT_USER);
          setMetadata(authData.metadata);
          navigate('/', { replace: true });
        } else {
          callback(authData);
        }
      } else {
        callback();
      }
    },
    [navigate]
  );

  const isAuthenticated = useCallback(() => !!token, [token]);

  const isLeader = useCallback(() => {
    if (metadata) {
      const userInfo = metadata[USER_INFO_ATTRIBUTE];
      return userInfo.item?.leadership ? userInfo.item.leadership : false;
    }
    return false;
  }, [metadata]);

  const isSelect = useCallback(() => {
    if (metadata) {
      const userInfo = metadata[USER_INFO_ATTRIBUTE];
      return userInfo.item?.select ? userInfo.item.select : false;
    }
    return false;
  }, [metadata]);

  const isDevelop = useCallback(() => {
    if (metadata) {
      const userInfo = metadata[USER_INFO_ATTRIBUTE];
      return userInfo.item?.develop ? userInfo.item.develop : false;
    }
    return false;
  }, [metadata]);

  const value = useMemo(
    () => ({
      user,
      userDisplayName,
      clientId,
      userId,
      isLeader,
      isDevelop,
      isSelect,
      metadata,
      clientDisplayName,
      token,
      slug,
      userRole,
      login,
      logout,
      loadSession,
      isAuthenticated,
    }),
    [
      user,
      userDisplayName,
      clientId,
      userId,
      isLeader,
      isDevelop,
      isSelect,
      metadata,
      clientDisplayName,
      token,
      slug,
      userRole,
      logout,
      loadSession,
      isAuthenticated,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth should be used within an AuthProvider.');
  }
  return context;
};

export function RequireAuth({ role, children }) {
  const { token, userRole } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (!token) {
    let path = '/login';
    const cid = searchParams.get('cid');
    if (cid) {
      path += `?cid=${cid}`;
    }

    return <Navigate to={path} state={{ from: location }} replace />;
  }

  if (role && role !== userRole) {
    return <Navigate to="/" replace />;
  }

  return children;
}
